import React from 'react'
import { View } from 'react-native'
import { Formik, FormikHelpers } from 'formik'
import { useSelector } from 'react-redux'
import * as yup from 'yup'

import tw from 'tailwind-rn'
import { HGTextInput } from '../../components/Form'
import { HGButton, HGText } from '../../components/HGUI'

const validationSchema = yup.object({
  currentUsername: yup.string().email().required(),
  username: yup
    .string()
    .notOneOf(
      [yup.ref('currentUsername')],
      'Alte und neue E-Mail-Adressen dürfen nicht identisch sein.'
    )
    .email('Bitte gib eine gültige E-Mail-Adresse an.')
    .required('Bitte gib eine gültige E-Mail-Adresse an.'),
  usernameConfirm: yup
    .string()
    .oneOf(
      [yup.ref('username'), null],
      'E-Mail-Adressen müssen übereinstimmen.'
    ),
})

export type ChangeEmailValues = {
  currentUsername: string
  username: string
  usernameConfirm: string
}

type Props = {
  onSubmit: (
    values: ChangeEmailValues,
    setSubmitting: (value: boolean) => void
  ) => void
}

const ChangeEmailForm = (props: Props) => {
  const { onSubmit } = props
  const { email: currentEmail } = useSelector((state) => state.user)

  const initialValues: ChangeEmailValues = {
    currentUsername: currentEmail,
    username: '',
    usernameConfirm: '',
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(
        values: ChangeEmailValues,
        { setSubmitting }: FormikHelpers<ChangeEmailValues>
      ) => {
        onSubmit(values, setSubmitting)
      }}
    >
      {({
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        values,
        errors,
      }) => (
        <View>
          <View>
            <HGTextInput
              onChangeText={handleChange('currentUsername')}
              onBlur={handleBlur('currentUsername')}
              value={values.currentUsername}
              placeholder="Aktuelle E-Mail-Adresse …"
              keyboardType="email-address"
              textContentType="emailAddress"
              editable={false}
            />

            <HGText style={tw('text-gray-600 mt-1 text-sm')}>
              Aktuelle E-Mail-Adresse
            </HGText>
          </View>

          <View style={tw('mt-4')}>
            <HGTextInput
              onChangeText={handleChange('username')}
              onBlur={handleBlur('username')}
              value={values.username}
              placeholder="Neue E-Mail-Adresse eingeben …"
              keyboardType="email-address"
              textContentType="emailAddress"
              autoCapitalize="none"
              autoCorrect={false}
            />

            <HGText style={tw('text-gray-600 mt-1 text-sm')}>
              Neue E-Mail-Adresse
            </HGText>

            {errors.username && (
              <HGText style={tw('text-sm mt-1 text-red-500')}>
                {errors.username}
              </HGText>
            )}
          </View>

          <View style={tw('mt-4')}>
            <HGTextInput
              onChangeText={handleChange('usernameConfirm')}
              onBlur={handleBlur('usernameConfirm')}
              value={values.usernameConfirm}
              placeholder="Neue E-Mail-Adresse wiederholen …"
              onSubmitEditing={() => handleSubmit}
              keyboardType="email-address"
              textContentType="emailAddress"
              autoCapitalize="none"
              autoCorrect={false}
            />

            <HGText style={tw('text-gray-600 mt-1 text-sm')}>
              Neue E-Mail-Adresse wiederholen
            </HGText>

            {errors.usernameConfirm && (
              <HGText style={tw('text-sm mt-1 text-red-500')}>
                {errors.usernameConfirm}
              </HGText>
            )}
          </View>

          <View style={tw('items-center')}>
            <HGButton
              disabled={isSubmitting || !isValid}
              title={!isSubmitting ? 'Bestätigen' : 'Bitte warten …'}
              onPress={handleSubmit}
              style={tw('mt-6 mb-6 w-full')}
            />
          </View>
        </View>
      )}
    </Formik>
  )
}

export default ChangeEmailForm
